import { Link } from "react-router";

import { Caution, HelpLayout, HelpSectionBlock } from "@/components/help-layout";
import { subText, table, td, th } from "@/components/table";
import { STEPS, TERMS } from "@/lib/help";

/**
 * How an offboarding works, start to finish.
 */
const SECTIONS = [
  { id: "steps", title: "What happens, in order" },
  { id: "raise", title: "Raising one" },
  { id: "phases", title: "Phase 1 and phase 2" },
  { id: "undo", title: "Revert, cancel, re-enable" },
  { id: "handover", title: "Mailbox and OneDrive handover" },
  { id: "refused", title: "What HROps refuses" },
  { id: "status", title: "Statuses in the queue" },
];

const STATUSES: Array<[string, string]> = [
  ["scheduled", "Waiting for the effective time. Cancel is free."],
  ["running", "Phase 1 is being executed right now."],
  ["phase 1 done", "Account disabled in both directories; the revert window is open."],
  ["partial", "Some step failed. Retried automatically; worth a look if it stays."],
  ["completed", "Phase 2 ran: mailbox shared, licence released."],
  ["failed", "Nothing was applied. Finished; raise it again if it should happen."],
  ["reverted", "Marked wrong in HROps after phase 1. Nothing re-enabled by itself."],
  ["cancelled", "Withdrawn before anything ran."],
  ["re-enabling", "Asked to put the person back; not done yet."],
  ["re-enabled", "The account is enabled again in Entra and on-prem AD."],
];

export function LeaversHelpPage() {
  return (
    <HelpLayout
      title="Leavers"
      intro="HR names who goes and when. At that time the account is disabled in Entra and in on-prem AD, sessions are revoked, devices are disabled and the mailbox is handed over. Two weeks later the irreversible half runs."
      sections={SECTIONS}
    >
      <HelpSectionBlock id="steps" title="What happens, in order">
        <ol className="space-y-3">
          {STEPS.map((step) => (
            <li key={step.title}>
              <h3 className="text-fg font-medium">{step.title}</h3>
              <p className={`${subText} mt-0.5`}>
                {step.body}
                {step.terms?.length ? (
                  <>
                    {" "}
                    {step.terms.map((termId, index) => (
                      <span key={termId}>
                        {index > 0 ? " · " : ""}
                        <Link to={`/help/glossary#term-${termId}`} className="underline">
                          {TERMS[termId].title}
                        </Link>
                      </span>
                    ))}
                  </>
                ) : null}
              </p>
            </li>
          ))}
        </ol>
      </HelpSectionBlock>

      <HelpSectionBlock id="raise" title="Raising one">
        <p>
          <Link className="underline" to="/leaver">
            New offboarding
          </Link>
          : type a surname and pick the person from the directory, so the sign-in name recorded is
          theirs and not a namesake&apos;s. Choose Now, or a date, a time and the zone that time is
          meant in. Name who takes the mailbox and who takes the OneDrive; they can be different
          people. Confirm. Nothing happens until the effective time.
        </p>
        <p>
          An exit reported on the Workforce page and approved there raises the offboarding the same
          way; it then appears in the queue like any other.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="phases" title="Phase 1 and phase 2">
        <p>
          <strong>Phase 1</strong>, at the effective time: account disabled in Entra and in on-prem
          AD, sessions revoked, registered devices disabled, the person hidden from the address
          book, Full Access on the mailbox and read access on the OneDrive granted to the named
          colleagues (the delegate still sees the hidden mailbox), and a Deactivate User Request in
          Ivanti for the IT helpdesk, with the person who raised the offboarding as the requester,
          for the rest of the leaver checklist. Everything in phase 1 can be undone.
        </p>
        <p>
          <strong>Phase 2</strong>, fourteen days later: the mailbox becomes a shared mailbox and
          the E5 licence is released, in that order. An online archive that was never used is
          removed, so the delegate sees one mailbox. A mailbox over 50 GB or on litigation hold
          keeps its E5, and the IT helpdesk gets a mail saying why. Other licences, such as D365,
          are not touched. Nothing in phase 2 can be undone from HROps.
        </p>
        <Caution>
          On-prem access stops within seconds. Mail, files and Teams follow within about fifteen
          minutes; anything else can hold a token for up to an hour. If somebody must be out of
          everything this minute, call SecOps.
        </Caution>
      </HelpSectionBlock>

      <HelpSectionBlock id="undo" title="Revert, cancel, re-enable">
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Cancel</strong> before the effective time: nothing ran, the record is closed.
          </li>
          <li>
            <strong>Revert</strong> after phase 1: marks the record as wrong and stops phase 2. It
            re-enables nothing by itself.
          </li>
          <li>
            <strong>Re-enable the account</strong>: puts the person back, in Entra and on-prem AD,
            with a reason. Only on a record HROps itself ran. After phase 2 it does not bring the
            licence back or un-share the mailbox.
          </li>
        </ul>
      </HelpSectionBlock>

      <HelpSectionBlock id="handover" title="Mailbox and OneDrive handover">
        <p>
          The mailbox delegate gets Full Access; it appears in their Outlook by itself, usually
          within a few hours. Send As is off unless turned on, and is only for a customer-facing
          address that has to keep answering. The OneDrive delegate gets read access under Shared
          with me.
        </p>
        <Caution>
          The OneDrive is not permanent. The licence is released in phase 2 and the tenant keeps an
          unlicensed OneDrive for 93 days, so the files are gone roughly 107 days after the
          offboarding unless somebody moves them.
        </Caution>
      </HelpSectionBlock>

      <HelpSectionBlock id="refused" title="What HROps refuses">
        <p>
          Holders of a directory role in Entra, accounts in the protected administrative unit, and
          the break-glass emergency accounts are never offboarded by HROps, whoever asks. The form
          says so while the name is being typed; the worker re-checks at execution. Anything that
          cannot be checked counts as a refusal. Such a case is raised with SecOps, not scheduled
          here.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="status" title="Statuses in the queue">
        <table className={table}>
          <thead>
            <tr>
              <th className={th}>Status</th>
              <th className={th}>Means</th>
            </tr>
          </thead>
          <tbody>
            {STATUSES.map(([s, m]) => (
              <tr key={s}>
                <td className={`${td} font-medium whitespace-nowrap`}>{s}</td>
                <td className={`${td} text-fg-muted`}>{m}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </HelpSectionBlock>
    </HelpLayout>
  );
}
